import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdAccountBalanceWallet, MdUploadFile } from 'react-icons/md';
import { useFinance } from 'contexts/FinanceContext';
import { fetchWalletTransfers } from 'lib/api';
import {
  ImportRow,
  isDuplicateTransaction,
  makeImportId,
  monthKeyFromDate,
  monthLabel,
  parseImportFile,
} from 'lib/import';
import { TransactionType } from 'types/finance';

type TypeFilter = 'all' | TransactionType;

interface ImportTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImportTransactionsModal({
  isOpen,
  onClose,
}: ImportTransactionsModalProps) {
  const {
    addTransactions,
    getCategoriesByType,
    currencySymbol,
    transactions,
  } = useFinance();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<ImportRow[]>([]);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [walletFilter, setWalletFilter] = useState('all');
  const [incomeCategoryId, setIncomeCategoryId] = useState('');
  const [expenseCategoryId, setExpenseCategoryId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileName, setFileName] = useState('');

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const boxBg = useColorModeValue('secondaryGray.300', 'navy.900');

  const incomeCats = getCategoriesByType('income');
  const expenseCats = getCategoriesByType('expense');

  useEffect(() => {
    if (!isOpen) return;
    setRows([]);
    setTypeFilter('all');
    setMonthFilter('all');
    setWalletFilter('all');
    setFileName('');
    setIncomeCategoryId(incomeCats[0]?.id || '');
    setExpenseCategoryId(expenseCats[0]?.id || '');
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthOptions = useMemo(() => {
    const keys = new Set(rows.map((r) => monthKeyFromDate(r.date)).filter(Boolean));
    return Array.from(keys).sort().reverse();
  }, [rows]);

  const walletOptions = useMemo(() => {
    const keys = new Set(
      rows.map((r) => (r.wallet || '').toLowerCase()).filter(Boolean)
    );
    return Array.from(keys).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (monthFilter !== 'all' && monthKeyFromDate(r.date) !== monthFilter) {
        return false;
      }
      if (
        walletFilter !== 'all' &&
        (r.wallet || '').toLowerCase() !== walletFilter
      ) {
        return false;
      }
      return true;
    });
  }, [rows, typeFilter, monthFilter, walletFilter]);

  const markedVisible = visibleRows.filter((r) => r.marked && !r.duplicate);
  const allVisibleMarked =
    visibleRows.filter((r) => !r.duplicate).length > 0 &&
    visibleRows.filter((r) => !r.duplicate).every((r) => r.marked);

  const annotateDuplicates = (list: ImportRow[]): ImportRow[] =>
    list.map((r) => {
      const duplicate = isDuplicateTransaction(r, transactions);
      return {
        ...r,
        duplicate,
        marked: duplicate ? false : r.marked,
      };
    });

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setLoading(true);
    setFileName(file.name);
    try {
      const result = await parseImportFile(file);
      setRows(
        annotateDuplicates(result.rows.map((r) => ({ ...r, marked: true })))
      );
      setMonthFilter('all');
      setWalletFilter('all');
      if (!result.rows.length) {
        toast({
          title: 'No transactions found',
          description: result.warnings[0] || 'Try another file.',
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: `${result.rows.length} rows ready`,
          description:
            result.warnings[0] ||
            'Filter by type/month/wallet, mark rows, then add.',
          status: 'success',
          duration: 4000,
        });
      }
    } catch (err) {
      console.error(err);
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Could not read file.',
        status: 'error',
        duration: 5000,
      });
      setRows([]);
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleFetchWallets = async () => {
    setLoading(true);
    setFileName('Saved wallets');
    try {
      const result = await fetchWalletTransfers();
      const mapped: ImportRow[] = result.rows.map((r, i) => ({
        id: makeImportId(i),
        date: r.date,
        type: r.type,
        amount: r.amount,
        description: r.description,
        reference: r.reference,
        wallet: r.walletLabel || r.wallet || r.chain,
        marked: true,
        tokenSymbol: r.tokenSymbol,
        tokenAmount: r.tokenAmount,
        amountUsdt: r.amountUsdt,
        source: 'wallet',
      }));
      const annotated = annotateDuplicates(mapped);
      setRows(annotated);
      setMonthFilter('all');
      setWalletFilter('all');
      const dupCount = annotated.filter((r) => r.duplicate).length;
      toast({
        title: `${annotated.length} wallet transfers loaded`,
        description:
          result.warnings[0] ||
          (dupCount
            ? `${dupCount} already saved (skipped by default).`
            : `Amounts shown in BDT (USDT ৳${result.usdtPriceBdt}).`),
        status: annotated.length ? 'success' : 'warning',
        duration: 4500,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: 'Wallet fetch failed',
        description:
          err instanceof Error
            ? err.message
            : 'Make sure the API is running on port 4000.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id || r.duplicate) return r;
        return { ...r, marked: !r.marked };
      })
    );
  };

  const toggleAllVisible = () => {
    const next = !allVisibleMarked;
    const ids = new Set(
      visibleRows.filter((r) => !r.duplicate).map((r) => r.id)
    );
    setRows((prev) =>
      prev.map((r) => (ids.has(r.id) ? { ...r, marked: next } : r))
    );
  };

  const markAllFiltered = (value: boolean) => {
    const ids = new Set(
      visibleRows.filter((r) => !r.duplicate).map((r) => r.id)
    );
    setRows((prev) =>
      prev.map((r) => (ids.has(r.id) ? { ...r, marked: value } : r))
    );
  };

  const handleAddMarked = async () => {
    const selected = rows.filter((r) => r.marked && !r.duplicate);
    const filtered = selected.filter((r) => {
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (monthFilter !== 'all' && monthKeyFromDate(r.date) !== monthFilter) {
        return false;
      }
      if (
        walletFilter !== 'all' &&
        (r.wallet || '').toLowerCase() !== walletFilter
      ) {
        return false;
      }
      return true;
    });

    if (!filtered.length) {
      toast({
        title: 'Nothing selected',
        description: 'Mark at least one new row to add.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!incomeCategoryId || !expenseCategoryId) {
      toast({
        title: 'Pick categories',
        description: 'Choose default income and expense categories.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      const result = addTransactions(
        filtered.map((r) => ({
          type: r.type,
          amount: r.amount,
          categoryId: r.type === 'income' ? incomeCategoryId : expenseCategoryId,
          description: r.description,
          date: r.date,
          reference: r.reference,
          wallet: r.wallet,
          marked: false,
          source: r.source || (r.tokenSymbol ? 'wallet' : 'import'),
          tokenSymbol: r.tokenSymbol as any,
          tokenAmount: r.tokenAmount,
          amountUsdt: r.amountUsdt,
        }))
      );
      toast({
        title: `${result.added} added`,
        description:
          result.skipped > 0
            ? `${result.skipped} skipped (already existed).`
            : undefined,
        status: 'success',
        duration: 3500,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>Import transactions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction={{ base: 'column', md: 'row' }} gap="12px" mb="16px" wrap="wrap">
            <Input
              ref={fileRef}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls,application/pdf,text/csv"
              display="none"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
            />
            <Button
              leftIcon={<Icon as={MdUploadFile} />}
              variant="brand"
              borderRadius="16px"
              h="40px"
              onClick={() => fileRef.current?.click()}
              isLoading={loading}
            >
              Choose PDF / CSV / Excel
            </Button>
            <Button
              leftIcon={<Icon as={MdAccountBalanceWallet} />}
              bg={boxBg}
              color={textColor}
              borderRadius="16px"
              h="40px"
              onClick={handleFetchWallets}
              isLoading={loading}
            >
              Fetch from wallet
            </Button>
            {fileName && (
              <Box alignSelf="center" fontSize="sm" color="secondaryGray.600">
                {fileName}
              </Box>
            )}
          </Flex>

          <Flex
            gap="12px"
            wrap="wrap"
            mb="16px"
            align={{ base: 'stretch', md: 'flex-end' }}
          >
            <FormControl maxW="160px">
              <FormLabel fontSize="sm" fontWeight="600">
                Type
              </FormLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
                borderRadius="16px"
                bg={boxBg}
              >
                <option value="all">All</option>
                <option value="income">Income only</option>
                <option value="expense">Expense only</option>
              </Select>
            </FormControl>
            <FormControl maxW="180px">
              <FormLabel fontSize="sm" fontWeight="600">
                Month
              </FormLabel>
              <Select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                borderRadius="16px"
                bg={boxBg}
              >
                <option value="all">All months</option>
                {monthOptions.map((key) => (
                  <option key={key} value={key}>
                    {monthLabel(key)}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl maxW="160px">
              <FormLabel fontSize="sm" fontWeight="600">
                Wallet
              </FormLabel>
              <Select
                value={walletFilter}
                onChange={(e) => setWalletFilter(e.target.value)}
                borderRadius="16px"
                bg={boxBg}
              >
                <option value="all">All wallets</option>
                {walletOptions.map((w) => (
                  <option key={w} value={w}>
                    {w.toUpperCase()}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl maxW="200px">
              <FormLabel fontSize="sm" fontWeight="600">
                Income category
              </FormLabel>
              <Select
                value={incomeCategoryId}
                onChange={(e) => setIncomeCategoryId(e.target.value)}
                borderRadius="16px"
                bg={boxBg}
              >
                {incomeCats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl maxW="200px">
              <FormLabel fontSize="sm" fontWeight="600">
                Expense category
              </FormLabel>
              <Select
                value={expenseCategoryId}
                onChange={(e) => setExpenseCategoryId(e.target.value)}
                borderRadius="16px"
                bg={boxBg}
              >
                {expenseCats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Button
              borderRadius="16px"
              h="40px"
              bg={boxBg}
              onClick={() => markAllFiltered(true)}
              isDisabled={!visibleRows.some((r) => !r.duplicate)}
            >
              Mark all shown
            </Button>
            <Button
              borderRadius="16px"
              h="40px"
              bg={boxBg}
              onClick={() => markAllFiltered(false)}
              isDisabled={!visibleRows.some((r) => !r.duplicate)}
            >
              Unmark all shown
            </Button>
          </Flex>

          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner color="brand.500" />
            </Flex>
          ) : (
            <Box
              maxH="420px"
              overflowY="auto"
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="16px"
            >
              <Table size="sm">
                <Thead position="sticky" top={0} bg={boxBg} zIndex={1}>
                  <Tr>
                    <Th>
                      <Checkbox
                        isChecked={allVisibleMarked}
                        isIndeterminate={
                          markedVisible.length > 0 && !allVisibleMarked
                        }
                        onChange={toggleAllVisible}
                      />
                    </Th>
                    <Th>Date</Th>
                    <Th>Wallet</Th>
                    <Th>Type</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Description</Th>
                    <Th>Reference</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {visibleRows.length === 0 ? (
                    <Tr>
                      <Td colSpan={7}>
                        <Text
                          py="24px"
                          textAlign="center"
                          color="secondaryGray.600"
                        >
                          Upload a file or fetch from wallet to preview rows.
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    visibleRows.map((row) => (
                      <Tr
                        key={row.id}
                        opacity={row.duplicate ? 0.55 : 1}
                        bg={row.duplicate ? 'blackAlpha.50' : undefined}
                      >
                        <Td>
                          <Checkbox
                            isChecked={row.marked}
                            isDisabled={row.duplicate}
                            onChange={() => toggleRow(row.id)}
                          />
                        </Td>
                        <Td whiteSpace="nowrap">{row.date}</Td>
                        <Td textTransform="uppercase" fontSize="xs">
                          {row.wallet || '—'}
                        </Td>
                        <Td>
                          <Text
                            color={
                              row.type === 'income' ? 'green.500' : 'red.500'
                            }
                            fontWeight="600"
                            textTransform="capitalize"
                          >
                            {row.type}
                          </Text>
                        </Td>
                        <Td isNumeric whiteSpace="nowrap">
                          <Text fontWeight="700">
                            {currencySymbol}
                            {row.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Text>
                          {row.tokenSymbol && row.tokenAmount != null && (
                            <Text fontSize="xs" color="secondaryGray.600">
                              {row.tokenAmount} {row.tokenSymbol}
                              {row.amountUsdt != null
                                ? ` ≈ ${row.amountUsdt} USDT`
                                : ''}
                            </Text>
                          )}
                        </Td>
                        <Td>
                          <Text noOfLines={2} maxW="280px">
                            {row.description}
                            {row.duplicate ? ' (already saved)' : ''}
                          </Text>
                        </Td>
                        <Td>
                          <Text
                            noOfLines={1}
                            maxW="160px"
                            fontSize="xs"
                            title={row.reference}
                          >
                            {row.reference || '—'}
                          </Text>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          <Text mt="12px" fontSize="sm" color="secondaryGray.600">
            Showing {visibleRows.length} · Marked {markedVisible.length} ·
            Duplicates {visibleRows.filter((r) => r.duplicate).length}
          </Text>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" borderRadius="16px" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="brand"
            borderRadius="16px"
            onClick={handleAddMarked}
            isLoading={saving}
            isDisabled={!markedVisible.length}
          >
            Add marked ({markedVisible.length})
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
